import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import TwoFactorPrompt from "@/components/TwoFactorPrompt";

export default function Login() {
  const { setUser, checkAuth } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa && data.challenge_token) {
        setChallenge(data.challenge_token);
        return;
      }
      setUser(data);
      toast.success(`Welcome back, ${data.name.split(" ")[0]}`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Login failed", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  const onTfaSuccess = async (userData) => {
    setUser(userData);
    // Refresh in case backend sets org data / picture
    checkAuth && checkAuth();
    nav("/dashboard", { replace: true });
  };

  const cancelTfa = () => {
    setChallenge(null);
    setPassword("");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50 grid lg:grid-cols-2">
      {/* Left form */}
      <div className="flex items-center justify-start px-6 md:px-16 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center mb-10" data-testid="login-logo">
            <img
              src="/audentra-logo.png"
              alt="Audentra"
              className="h-12 w-auto"
            />
          </Link>

          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            {challenge ? "Two-factor authentication" : "Sign in to your account"}
          </h1>
          <p className="text-slate-600 mt-2">
            {challenge
              ? "Confirm it's you with the 6-digit code from your authenticator app."
              : "Workers, team members and managers all sign in here."}
          </p>

          {challenge ? (
            <TwoFactorPrompt
              challengeToken={challenge}
              onSuccess={onTfaSuccess}
              onCancel={cancelTfa}
            />
          ) : (
          <>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="label-caps">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-11 bg-teal-700 hover:bg-teal-800 rounded-full"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="text-sm text-teal-700 hover:text-teal-800 font-semibold"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
            <div className="flex-1 h-px bg-slate-200" /> OR CONTINUE WITH{" "}
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Button
            variant="outline"
            onClick={googleLogin}
            data-testid="login-google-btn"
            className="w-full h-11 rounded-full border-slate-300"
          >
            <img alt="" src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw" className="h-4 w-4 mr-2" />
            Continue with Google
          </Button>

          <p className="text-sm text-slate-600 mt-8">
            New worker?{" "}
            <Link to="/register" data-testid="link-register" className="text-teal-700 font-semibold hover:underline">
              Create an account
            </Link>
          </p>
          </>
          )}
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:block relative bg-slate-900 overflow-hidden">
        <img
          alt="Healthcare team"
          src="https://images.pexels.com/photos/7904485/pexels-photo-7904485.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/80 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="label-caps text-teal-300 mb-3">Trusted by care recruiters</div>
          <div className="font-heading text-3xl font-bold leading-tight max-w-md">
            Every rejection includes a reason — so workers know exactly what to fix.
          </div>
        </div>
      </div>
    </div>
  );
}
